/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Box,
  Divider,
  Flex,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Scrollbar,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  EnablePolicyRadioGroup,
  GmailNote,
  GoogleLoginSvg,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from './share';
import { useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '../../../utils/common';
import { ProvisionApi } from '@Apis';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '../../../types/provision';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { reportPendo } from '../../../utils/setupPendo';
import RejectProvisionModal from './rejectProvisionModal';
import { App, Service } from '@Types/app';

enum GmailStatus {
  NotProvision = 0,
  Provisioning = 1,
  Provisioned = 2,
}

const GmailModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const { t } = useTranslation(['provision', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: GmailStatus.NotProvision,
    [ProvisionStatusEnum.Provisioning]: GmailStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: GmailStatus.Provisioned,
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [gmailStatus, setGmailStatus] = useState<GmailStatus>(() => {
    if (Object.keys(StatusMap).includes(provisionStatus.status)) {
      return StatusMap[provisionStatus.status as keyof typeof StatusMap];
    } else {
      //provisionStatus.status = failure
      //token failure
      if (provisionStatus.authorization.status === TokenStatus.Failure) {
        return GmailStatus.NotProvision;
      } else {
        //id sync fail
        return GmailStatus.Provisioning;
      }
    }
  });
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (gmailStatus === GmailStatus.NotProvision) {
      ProvisionApi.apiGetAuthenticationInfo(Service.Gmail, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          console.error(JSON.stringify(e));
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (errorCode === RejectProvisionReason.ServiceConflict) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={Service.Gmail}
        reason={rejectReason}
      />
    );
  }

  if (gmailStatus === GmailStatus.NotProvision && !authenticationInfo) {
    return <></>;
  }

  return (
    <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        {/* TODO i18n: "Grant Access to Gmail" */}
        <ModalHeader>{t('provision.service.gmail.title')}</ModalHeader>
        <Scrollbar overflow="scroll" maxHeight="60vh">
          <ModalBody mt="0px !important">
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps
                direction="vertical"
                current={gmailStatus}
                items={[
                  {
                    title: (
                      <Text>
                        {t('provision.service.gmail.installApp')}{' '}
                        <Link
                          display="inline"
                          onClick={() => {
                            window.open(
                              authenticationInfo?.[0]?.installAppUrl || '',
                              '_blank'
                            );
                          }}
                        >
                          {t('common.action.label.clickHere')}
                        </Link>
                      </Text>
                    ),
                    description: <Space height="16px" />,
                    icon: <UserIcon />,
                  },
                  {
                    title: (
                      <Text color={colorStyle.color.primary}>
                        {t('provision.service.gmail.grantPermission')}
                      </Text>
                    ),
                    description:
                      gmailStatus === GmailStatus.NotProvision ? (
                        <GrantPermissionAndEnablePolicy
                          gmailStatus={gmailStatus}
                          setGmailStatus={setGmailStatus}
                          authenticationInfo={authenticationInfo}
                          setErrorMsg={setErrorMsg}
                          orgId={orgId}
                        />
                      ) : (
                        <Text color={colorStyle.color.tertiary}>
                          {t('provision.common.permissionGranted')}
                        </Text>
                      ),
                    icon: (
                      <StepIcon
                        checked={gmailStatus >= GmailStatus.Provisioning}
                      />
                    ),
                  },
                ]}
              />
              {gmailStatus === GmailStatus.Provisioning &&
                (syncDataFailFlag ? (
                  <DataSyncFailed
                    service={Service.Gmail}
                    onRetry={async () => {
                      await ProvisionApi.apiRetrySyncData(Service.Gmail, orgId);
                      setGmailStatus(GmailStatus.Provisioning);
                      setSyncDataFailFlag(false);
                    }}
                  />
                ) : (
                  <DataSyncing
                    onRefresh={async () => {
                      const res = await ProvisionApi.apiGetProvisionStatus(
                        Service.Gmail,
                        orgId
                      );
                      if (res.data.status === ProvisionStatusEnum.Provisioned) {
                        setGmailStatus(GmailStatus.Provisioned);
                      }
                    }}
                  />
                ))}
              {gmailStatus === GmailStatus.Provisioned && <DataSynced />}
              <GmailNote />
            </Flex>
          </ModalBody>
        </Scrollbar>
        <ModalFooter>
          <Button minWidth="20x" onClick={onClose}>
            {t('common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GmailModal;

const GrantPermissionAndEnablePolicy = ({
  gmailStatus,
  authenticationInfo,
  setGmailStatus,
  setErrorMsg,
  orgId,
}: {
  gmailStatus: GmailStatus;
  authenticationInfo: AuthenticationInfoRes | null;
  setGmailStatus: (status: GmailStatus) => void;
  setErrorMsg: (msg: string | undefined) => void;
  orgId: string;
}) => {
  const { t } = useTranslation('provision');
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, toggleLoading] = useToggle(false);
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });
  const timeRef = useRef<any>(null);
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: 'gmail',
    });
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const gmailProvisionInfo = getProvisionCookie('provision_status_21');
      if (gmailProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(gmailProvisionInfo);
        if (error) {
          setErrorMsg(t(`provision.service.gmail.error.deniedGrant`));
        } else {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.Gmail,
              code,
              radioGroupRef.current.enabledPolicyType,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.gmail.error.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setGmailStatus(GmailStatus.Provisioning);
            }
          } catch (e) {
            setErrorMsg(t('common.apiException.internalError'));
            console.error(JSON.stringify(e));
          }
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Flex direction="column" gap="16px" color={colorStyle.color.primary}>
        <Space />
        <Divider variant="dashed" orientation="horizontal" />
        <Text color={colorStyle.color.secondary}>
          {/* TODO: i18n "Select the policy to enable when the access grant is completed:" */}
          {t('provision.common.selectPolicyToEnable')}
        </Text>
        <EnablePolicyRadioGroup service={Service.Gmail} ref={radioGroupRef} />
        <Divider variant="dashed" orientation="horizontal" />
        <Box>
          <Button
            px="0px"
            borderWidth="0px"
            variant="ghost"
            disabled={!(gmailStatus === GmailStatus.NotProvision)}
            onClick={onClick}
          >
            <GoogleLoginSvg
              disabled={!(gmailStatus === GmailStatus.NotProvision)}
            />
          </Button>
        </Box>
      </Flex>
    </>
  );
};
